import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    ENVIRONMENT,
    PaperlessInfo,
    PaperlessStatusType,
    UpdatePaperlessResponse,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';

const PAPERLESS_DISPLAY_STATUS_KEY = 'PaperlessDisplayStatus';

const PAPERLESS_REMIND_LATER_KEY = 'PaperlessRemindLaterTimestamp';

const PAPERLESS_NO_THANKS_KEY = 'PaperlessNoThanks';

const REMIND_LATER_EXPIRY_DAYS = 30;

export type { PaperlessStatusType, UpdatePaperlessResponse } from '@ld.consumer-portal/models';
import { LoggingService } from '@ld.consumer-portal/utils';

export interface UpdatePaperlessRequest {
  loanNumber: string;
  encryptionValue: string;
  paperless: Omit<PaperlessInfo, 'isEditable' | 'isEligible' | 'isEnabled'>;
}

@Injectable({
  providedIn: 'root',
})
export class PaperlessService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);
  private readonly loggingService = inject(LoggingService);

  async setUserPaperlessPreference(
    loanNumber: string,
    encryptionValue: string,
    status: PaperlessStatusType,
  ): Promise<void> {
    try {
      const url = `${this.env.appSettings.melloServDomainUrl}/${this.env.appSettings.apiNamespace}/MelloServDomain/LoanInformation/SetPaperlessStatus`;

      const body = {
        loanNumber,
        encryptionValue,
        paperless: this.mapStatusToApiValue(status),
      };

      await firstValueFrom(this.http.post(url, body));
    } catch (error) {
      this.loggingService.logException(error, 'Error setting paperless status:');
      // Don't throw - allow navigation to continue even if API fails
    }
  }

  async updateLoanPaperlessSettings(request: UpdatePaperlessRequest): Promise<UpdatePaperlessResponse> {
    try {
      const url = `${this.env.appSettings.ServicingLINCPortalBorrowers}/${
        this.env.appSettings.apiNamespace
      }/ServicingLINCPortal/Preference/Paperless/${request.loanNumber}?encryptionValue=${encodeURIComponent(
        request.encryptionValue,
      )}`;

      const body = {
        isBillingPaperless: request.paperless.isBillingPaperless,
        isTaxPaperless: request.paperless.isTaxPaperless,
        isEscrowPaperless: request.paperless.isEscrowPaperless,
        isOtherLettersPaperless: request.paperless.isOtherLettersPaperless,
      };

      const response = await firstValueFrom(this.http.post<UpdatePaperlessResponse>(url, body));
      return response;
    } catch (error) {
      this.loggingService.logException(error, 'Error updating paperless settings:');
      throw error;
    }
  }

  private mapStatusToApiValue(status: PaperlessStatusType): number {
    switch (status) {
      case 'go_paperless':
        return 3;
      case 'remind_later':
        return 1;
      case 'no_thanks':
        return 2;
      default:
        return 1;
    }
  }

  shouldShowPaperlessPromo(showPaperLessPromo: boolean, loanNumber?: string): boolean {
    if (!showPaperLessPromo) {
      return false;
    }

    const displayKey = loanNumber ? `${PAPERLESS_DISPLAY_STATUS_KEY}_${loanNumber}` : PAPERLESS_DISPLAY_STATUS_KEY;
    const displayStatus = localStorage.getItem(displayKey);
    if (displayStatus !== null) {
      return false;
    }

    const remindKey = loanNumber ? `${PAPERLESS_REMIND_LATER_KEY}_${loanNumber}` : PAPERLESS_REMIND_LATER_KEY;
    const remindLaterTimestamp = localStorage.getItem(remindKey);
    if (remindLaterTimestamp !== null) {
      const remindLaterDate = new Date(remindLaterTimestamp);
      const expiryDate = new Date(remindLaterDate.getTime() + REMIND_LATER_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      if (new Date() < expiryDate) {
        return false;
      }
    }

    return true;
  }

  setRemindLaterTimestamp(loanNumber?: string): void {
    const key = loanNumber ? `${PAPERLESS_REMIND_LATER_KEY}_${loanNumber}` : PAPERLESS_REMIND_LATER_KEY;
    localStorage.setItem(key, new Date().toISOString());
  }

  setNoThanksPreference(loanNumber?: string): void {
    const key = loanNumber ? `${PAPERLESS_NO_THANKS_KEY}_${loanNumber}` : PAPERLESS_NO_THANKS_KEY;
    localStorage.setItem(key, 'true');
  }

  setGoPaperlessPreference(loanNumber?: string): void {
    const key = loanNumber ? `${PAPERLESS_DISPLAY_STATUS_KEY}_${loanNumber}` : PAPERLESS_DISPLAY_STATUS_KEY;
    localStorage.setItem(key, 'true');
  }

  resetPaperlessDisplayStatus(): void {
    localStorage.removeItem(PAPERLESS_DISPLAY_STATUS_KEY);
    localStorage.removeItem(PAPERLESS_REMIND_LATER_KEY);
    localStorage.removeItem(PAPERLESS_NO_THANKS_KEY);
  }

  shouldShowPaperlessModal(showPaperLessPromo: boolean | undefined, loanNumber?: string): boolean {
    if (!showPaperLessPromo) {
      return false;
    }

    const displayKey = loanNumber ? `${PAPERLESS_DISPLAY_STATUS_KEY}_${loanNumber}` : PAPERLESS_DISPLAY_STATUS_KEY;
    const displayStatus = localStorage.getItem(displayKey);
    if (displayStatus !== null) {
      return false;
    }

    const noThanksKey = loanNumber ? `${PAPERLESS_NO_THANKS_KEY}_${loanNumber}` : PAPERLESS_NO_THANKS_KEY;
    const noThanks = localStorage.getItem(noThanksKey);
    if (noThanks === 'true') {
      return false;
    }

    const remindKey = loanNumber ? `${PAPERLESS_REMIND_LATER_KEY}_${loanNumber}` : PAPERLESS_REMIND_LATER_KEY;
    const remindLaterTimestamp = localStorage.getItem(remindKey);
    if (remindLaterTimestamp !== null) {
      const remindLaterDate = new Date(remindLaterTimestamp);
      const expiryDate = new Date(remindLaterDate.getTime() + REMIND_LATER_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      if (new Date() < expiryDate) {
        return false;
      }
      localStorage.removeItem(remindKey);
    }

    return true;
  }
}
